import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { t } from '@/i18n';
import type { DashboardMonthlyTagCost } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { MONTH_LABELS, formatAxis, monthKey, plnTooltipFormatter } from './chart-utils';

interface CostsByTagChartProps {
  data: DashboardMonthlyTagCost[];
  year: number;
}

export function CostsByTagChart({ data, year }: CostsByTagChartProps) {
  // Unikalne tagi z kolorami (kolejność pierwszego wystąpienia)
  const tagColorMap = new Map<string, string>();
  for (const d of data) {
    if (!tagColorMap.has(d.tagName)) tagColorMap.set(d.tagName, d.tagColor);
  }
  const tagNames = Array.from(tagColorMap.keys());

  // Wide format: wiersz per miesiąc, kolumna per tag (klucz = t0, t1, ... — bezpieczny)
  const keyFor = (i: number) => `t${i}`;
  const chartData = MONTH_LABELS.map((label, m) => {
    const row: Record<string, number | string> = { month: label };
    tagNames.forEach((tagName, i) => {
      const found = data.find((d) => d.month === monthKey(year, m) && d.tagName === tagName);
      row[keyFor(i)] = found?.totalNet ?? 0;
    });
    return row;
  });

  const config: ChartConfig = Object.fromEntries(
    tagNames.map((name, i) => [keyFor(i), { label: name }])
  );

  return (
    <ChartCard title={t('dashboard.monthlyTagCosts')}>
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        {tagNames.map((name) => (
          <span key={name} className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <span
              className="size-2.5 shrink-0 rounded-[2px]"
              style={{ background: tagColorMap.get(name) }}
            />
            {name}
          </span>
        ))}
      </div>
      <ChartContainer config={config} className="h-[300px] w-full">
        <BarChart data={chartData}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={64} />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(label) => `${label} ${year}`}
                formatter={plnTooltipFormatter(config, { hideZero: true })}
              />
            }
          />
          {tagNames.map((name, i) => (
            <Bar
              key={name}
              dataKey={keyFor(i)}
              stackId="tags"
              fill={tagColorMap.get(name)}
              stroke="var(--card)"
              strokeWidth={1}
              maxBarSize={32}
            />
          ))}
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
